import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { GripVertical } from 'lucide-react'
import type { FeedbackItem, ResolveFeedbackInput } from '../../index.js'
import { FeedbackRow } from './FeedbackRow.js'
import { moveItem } from './moveItem.js'

interface OpenFeedbackListProps {
  items: FeedbackItem[]
  onReorder: (feedbackIds: string[]) => void
  onResolve: (feedbackId: string, decision: ResolveFeedbackInput) => void
  resolvingId: string | null
  /** Whether an item arrived since the admin's previous sign-in. */
  isNew?: (item: FeedbackItem) => boolean
  /** Something to do when the list is empty - a way to add the first item. */
  emptyAction?: ReactNode
  /** Gate 1 for an item, and the item being started. */
  onDevelop?: (feedbackId: string) => void
  developingId?: string | null
}

/**
 * The open items in priority order. Drag a row by its handle (or use the
 * arrows) to move it; the new order is sent as soon as it is dropped. The
 * order shown while dragging is local, so the list follows the pointer
 * without waiting on the API.
 */
export function OpenFeedbackList({ items, onReorder, onResolve, resolvingId, isNew, emptyAction, onDevelop, developingId = null }: OpenFeedbackListProps) {
  const [order, setOrder] = useState(items)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  // Whether the drag in progress ended on a row; one that did not is reverted.
  const dropped = useRef(false)

  // Takes the server's order when the items themselves change (a new item,
  // a resolution, a reorder answered) - not on every render of the parent,
  // which would snap a just-dropped row back while its order is being saved.
  const sequence = items.map((item) => item.feedbackId).join('\n')
  useEffect(() => {
    setOrder(items)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sequence` stands for `items`
  }, [sequence])

  function indexOf(feedbackId: string): number {
    return order.findIndex((item) => item.feedbackId === feedbackId)
  }

  function commit(next: FeedbackItem[]): void {
    setOrder(next)
    if (next.some((item, index) => item.feedbackId !== items[index]?.feedbackId)) {
      onReorder(next.map((item) => item.feedbackId))
    }
  }

  function move(feedbackId: string, delta: number): void {
    const from = indexOf(feedbackId)
    commit(moveItem(order, from, from + delta))
  }

  function handleDragOver(event: DragEvent<HTMLLIElement>, overId: string): void {
    event.preventDefault()
    if (draggingId === null || draggingId === overId) {
      return
    }
    const from = indexOf(draggingId)
    const to = indexOf(overId)
    if (from !== -1 && to !== -1) {
      setOrder(moveItem(order, from, to))
    }
  }

  function handleDrop(event: DragEvent<HTMLLIElement>): void {
    event.preventDefault()
    dropped.current = true
    setDraggingId(null)
    commit(order)
  }

  /** A drag that ended anywhere but on a row (a gap, outside the list, Escape) goes back to the saved order. */
  function handleDragEnd(): void {
    setDraggingId(null)
    if (!dropped.current) {
      setOrder(items)
    }
    dropped.current = false
  }

  if (order.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[11px] text-ink-mid">Nothing open. New feedback from the apps lands here, or add something yourself.</p>
        {emptyAction}
      </div>
    )
  }

  return (
    <ol className="flex flex-col gap-2">
      {order.map((item, index) => (
        <li
          key={item.feedbackId}
          draggable
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', item.feedbackId)
            dropped.current = false
            setDraggingId(item.feedbackId)
          }}
          onDragOver={(event) => handleDragOver(event, item.feedbackId)}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
          className={`flex gap-2 rounded border bg-white p-3 ${draggingId === item.feedbackId ? 'border-coral opacity-60' : 'border-marine/15'}`}
        >
          <span aria-hidden="true" title="Drag to reprioritise" className="cursor-grab text-ink-mid/50 hover:text-ink-mid pt-0.5 active:cursor-grabbing">
            <GripVertical className="h-4 w-4" />
          </span>
          <FeedbackRow
            item={item}
            position={index + 1}
            isNew={isNew?.(item) ?? false}
            isResolving={resolvingId === item.feedbackId}
            onResolve={(decision) => onResolve(item.feedbackId, decision)}
            onMoveUp={index > 0 ? () => move(item.feedbackId, -1) : undefined}
            onMoveDown={index < order.length - 1 ? () => move(item.feedbackId, 1) : undefined}
            onDevelop={onDevelop ? () => onDevelop(item.feedbackId) : undefined}
            isDeveloping={developingId === item.feedbackId}
          />
        </li>
      ))}
    </ol>
  )
}
